import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAttendance } from '../contexts/AttendanceContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
} from '../components/ui/alert-dialog';
import CameraCapture from '../components/CameraCapture';
import {
  Clock, MapPin, TrendingUp, LogIn, LogOut,
  CheckCircle, AlertCircle, Loader2, RefreshCw, Camera, Bell,
  Sun, Sunrise, Moon, CloudSun, Timer, Target, Award, Zap,
} from 'lucide-react';
import { formatTime, getDayLabel, calculateDuration, formatDuration } from '../utils/dateFormatter';
import { toast } from 'sonner';
import { differenceInMinutes, parseISO, format, startOfWeek, addDays, subDays } from 'date-fns';
import { notificationManager } from '../utils/notificationManager';
import { storage } from '../utils/storage';
import { settingsService } from '../services/settings';
import type { AttendanceRecord, ShiftSettings, OfficeSettings } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

// Lazy-load the map to avoid SSR issues and reduce initial bundle size
const AttendanceMap = lazy(() => import('../components/AttendanceMap'));
const threshold = Number(process.env.REACT_APP_THRESHOLD_CHECK_IN_MINUTES ?? 60);

type StatusColor = 'success' | 'warning' | 'secondary';

function getGreeting(hour: number): { icon: React.ElementType } {
  if (hour < 6) return { icon: Moon };
  if (hour < 12) return { icon: Sunrise };
  if (hour < 17) return { icon: Sun };
  return { icon: CloudSun };
}

function getShiftProgressPercent(startTime: string, endTime: string, date?: string): number {
  if (!startTime || !endTime) return 0;
  const now = new Date();
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const start = new Date(date ?? new Date()); start.setHours(sh, sm, 0, 0);
  const end = new Date(date ?? new Date()); end.setHours(eh, em, 0, 0);

  // Handle overnight shifts (e.g., 17:00 - 02:00)
  if (isOvernightShift(startTime, endTime)) {
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    if (nowMinutes >= startMinutes) {
      // First half: shift in progress, ends tomorrow
      end.setDate(end.getDate() + 1);
    } else if (nowMinutes < endMinutes) {
      // Second half: after midnight, before shift end — started yesterday
      start.setDate(start.getDate() - 1);
    } else {
      end.setDate(end.getDate() + 1);
    }
  }

  const total = end.getTime() - start.getTime();
  if (total <= 0) return 0;
  const elapsed = now.getTime() - start.getTime();
  if (elapsed <= 0) return 0;
  return Math.min(100, Math.round((elapsed / total) * 100));
}

// Returns true when shift spans midnight (e.g. 17:00 – 02:00)
function isOvernightShift(startTime: string, endTime: string): boolean {
  if (!startTime || !endTime) return false;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  return eh * 60 + em < sh * 60 + sm;
}

// Returns how many minutes early the user is checking out (>0 means early)
function getEarlyLeaveMinutes(startTime: string, endTime: string): number {
  if (!endTime) return 0;
  const [eh, em] = endTime.split(':').map(Number);
  const shiftEnd = new Date();
  shiftEnd.setHours(eh, em, 0, 0);
  // Overnight shift (e.g. 17:00–02:00): shift ends on the NEXT calendar day
  // when the current time is still in the evening portion (after shift start hour).
  if (isOvernightShift(startTime, endTime)) {
    const now = new Date();
    const [sh, sm] = startTime.split(':').map(Number);
    if (now.getHours() * 60 + now.getMinutes() >= sh * 60 + sm) {
      shiftEnd.setDate(shiftEnd.getDate() + 1);
    }
  }
  const diff = differenceInMinutes(shiftEnd, new Date());
  return diff > 0 ? diff : 0;
}

export default function Dashboard(): React.JSX.Element {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { todayStatus, loading, monthlySummary, history, checkIn, checkOut, refresh, loadHistory, loadMonthlySummary } = useAttendance();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [workingDuration, setWorkingDuration] = useState('0h 0m');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState<'check_in' | 'check_out'>('check_in');
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>('default');
  const [showEarlyCheckoutModal, setShowEarlyCheckoutModal] = useState(false);
  const [showCheckInBlockedModal, setShowCheckInBlockedModal] = useState(false);
  // null = still loading from API, true = assigned, false = not assigned
  const [hasSiteAssigned, setHasSiteAssigned] = useState<boolean | null>(null);
  const [hasShiftAssigned, setHasShiftAssigned] = useState<boolean | null>(null);
  // Queue flag: set when navigating here via Camera FAB; fired once activeStatus is known
  // Start from localStorage so the UI is instant, then update from API
  const [officeSettings, setOfficeSettings] = useState<OfficeSettings>(storage.getOfficeSettings());
  const [shiftSettings, setShiftSettings] = useState<ShiftSettings>(storage.getShiftSettings());

  // Load page-specific data: history (weekly streak) and monthly summary (stats cards).
  useEffect(() => {
    void loadHistory();
    void loadMonthlySummary();
  }, [loadHistory, loadMonthlySummary]);

  useEffect(() => {
    // Load office sites from API and use the first as the active site
    settingsService.getSites().then((sites) => {
      if (sites.length > 0) {
        const s = sites[0];
        const o: OfficeSettings = { name: s.name, lat: s.latitude, lng: s.longitude, allowedRadius: s.radius_meter };
        setOfficeSettings(o);
        storage.setOfficeSettings(o);
        setHasSiteAssigned(true);
      } else {
        setHasSiteAssigned(false);
      }
    }).catch(() => {
      // Network error — don't block the user; backend will validate
      setHasSiteAssigned(true);
    });

    // Load assigned shifts from API and use the first as the active shift
    settingsService.getMyShifts().then((shifts) => {
      if (shifts.length > 0) {
        const sh = shifts[0];
        const s: ShiftSettings = { name: sh.name, startTime: sh.start_time, endTime: sh.end_time };
        setShiftSettings(s);
        storage.setShiftSettings(s);
        setHasShiftAssigned(true);
      } else {
        setHasShiftAssigned(false);
      }
    }).catch(() => {
      // Network error — don't block the user; backend will validate
      setHasShiftAssigned(true);
    });
  }, []);

  // Minutes until next shift start — recomputes every second via currentTime tick.
  const minutesUntilShiftStart = useMemo(() => {
    if (!shiftSettings.startTime) return Infinity;
    const todayDate = todayStatus?.date || new Date();
    const todayShiftDate = format(todayDate, 'yyyy-MM-dd') + ' ' + todayStatus?.shift.startTime;
    const threshold = todayStatus?.clockOut && todayStatus?.date === format(new Date(), 'yyyy-MM-dd') ? 1 : 0;
    const nextShiftDate = format(addDays(todayDate, threshold), 'yyyy-MM-dd') + ' ' + shiftSettings.startTime;
    const minutes = differenceInMinutes(nextShiftDate, todayShiftDate);
    return minutes;
  }, [shiftSettings.startTime, currentTime]);
  // console.log({minutesUntilShiftStart});

  // Overnight shift support: when the shift spans midnight (e.g. 17:00–02:00) and the clock
  // rolls to a new calendar day, todayStatus is null but yesterday's record may still be
  // open (clockOut is null). Treat that record as the "active" session so the Check-Out
  // button remains visible until the employee actually checks out.
  const overnightPendingCheckout = useMemo(() => {
    if (!isOvernightShift(shiftSettings.startTime, shiftSettings.endTime)) return null;
    if (todayStatus) return null; // normal day — todayStatus takes precedence
    const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
    return history.find((r) => r.date === yesterday && r.clockIn && !r.clockOut) ?? null;
  }, [history, todayStatus, shiftSettings.startTime, shiftSettings.endTime]);

  // Today's completed record (has both check-in and check-out)
  const todayCompletedRecord = useMemo(() => {
    if (todayStatus?.clockIn && todayStatus?.clockOut) {
      return todayStatus;
    }

    return null;
  }, [todayStatus]);

  // Yesterday's completed record (for overnight shifts like 17:00-02:00)
  // Show this in the morning until next check-in window opens
  const yesterdayCompletedRecord = useMemo(() => {
    const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
    return history.find((r) => r.date === yesterday && r.clockIn && r.clockOut) ?? null;
  }, [history]);

  // Any completed record to display (today or yesterday)
  const anyCompletedRecord = todayCompletedRecord ?? yesterdayCompletedRecord;

  // Check if we should show the check-in button
  const showCheckInButton = useMemo(() => {
    // If there's an active session (checked in but not out), don't show
    if (todayStatus && !todayStatus.clockOut) return false;

    // If we have a completed record (today or yesterday), check shift timing
    if (anyCompletedRecord) {
      if (!shiftSettings.startTime) return false;

      return minutesUntilShiftStart <= threshold;
    }

    // No record at all - show check-in button
    return true;
  }, [todayStatus, anyCompletedRecord, shiftSettings.startTime, minutesUntilShiftStart]);

  // The attendance record currently relevant for the UI.
  // Falls back to yesterday's open overnight record when todayStatus is null.
  // Also shows yesterday's completed record (for shift results display in morning).
  const activeStatus = useMemo(
    () => todayStatus ?? overnightPendingCheckout ?? todayCompletedRecord ?? yesterdayCompletedRecord ?? null,
    [todayStatus, overnightPendingCheckout, todayCompletedRecord, yesterdayCompletedRecord],
  );

  // Clock ticker
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Working duration counter
  useEffect(() => {
    if (activeStatus?.clockIn && !activeStatus?.clockOut) {
      const timer = setInterval(() => {
        const minutes = differenceInMinutes(new Date(), parseISO(activeStatus.clockIn));
        setWorkingDuration(formatDuration(minutes));
      }, 1000);
      return () => clearInterval(timer);
    } else if (activeStatus?.clockIn && activeStatus?.clockOut) {
      setWorkingDuration(calculateDuration(activeStatus.clockIn, activeStatus.clockOut));
    } else {
      setWorkingDuration('0h 0m');
    }
  }, [activeStatus]);

  // Schedule check-in reminder when component mounts and status changes
  useEffect(() => {
    setNotifPermission(notificationManager.getCurrentPermission());
    if (notificationManager.getSettings().enabled && shiftSettings.startTime) {
      notificationManager.scheduleCheckInReminder(
        shiftSettings.startTime,
        !!activeStatus?.clockIn
      );
    }
    if (notificationManager.getSettings().enabled && shiftSettings.endTime) {
      notificationManager.scheduleShiftEndReminder(
        shiftSettings.endTime,
        !!activeStatus?.clockOut,
        shiftSettings.startTime
      );
    }
    return () => {
      notificationManager.cancelReminder();
      notificationManager.cancelShiftEndReminder();
    };
  }, [activeStatus?.clockIn, activeStatus?.clockOut, shiftSettings.startTime, shiftSettings.endTime]);

  const handleEnableNotifications = async (): Promise<void> => {
    const permission = await notificationManager.requestPermission();
    setNotifPermission(permission);
    if (permission === 'granted') {
      toast.success(t.notifEnabled, {
        description: t.notifEnabledDesc,
      });
      if (shiftSettings.startTime) {
        notificationManager.scheduleCheckInReminder(
          shiftSettings.startTime,
          !!activeStatus?.clockIn
        );
      }
      if (shiftSettings.endTime) {
        notificationManager.scheduleShiftEndReminder(
          shiftSettings.endTime,
          !!activeStatus?.clockOut,
          shiftSettings.startTime
        );
      }
    } else {
      toast.error(t.notifPermDenied, {
        description: t.notifPermDeniedDesc,
      });
    }
  };

  const handleCheckInClick = (): void => {
    // Block check-in if site or shift is confirmed missing from API
    const missingAssignments = hasSiteAssigned === false || hasShiftAssigned === false;
    if (missingAssignments) {
      setShowCheckInBlockedModal(true);
      return;
    }
    setCameraMode('check_in');
    setShowCamera(true);
  };

  const handleCheckOutCameraOpen = (): void => {
    setCameraMode('check_out');
    setShowCamera(true);
  };

  const handleCameraCapture = async (photoDataUrl: string): Promise<void> => {
    setShowCamera(false);
    if (cameraMode === 'check_out') {
      await performCheckOut(photoDataUrl);
      return;
    }
    try {
      const record = await checkIn(photoDataUrl);
      notificationManager.cancelReminder();
      if (record.status === 'late' && record.checkInRecord?.lateMinutes) {
        void notificationManager.sendLateCheckInNotification(
          record.checkInRecord.lateMinutes,
          shiftSettings.startTime
        );
      }
      
      toast.success(t.checkedInToast, { description: t.checkedInDesc });
    } catch (error) {
      toast.error(t.checkInFailed, { description: (error as Error).message });
    }
  };

  const handleCameraClose = (): void => { setShowCamera(false); };

  const handleCheckOut = (): void => {
    // If still within shift hours, show early check-out confirmation first
    const earlyMinutes = getEarlyLeaveMinutes(shiftSettings.startTime, shiftSettings.endTime);
    if (earlyMinutes > 0) {
      setShowEarlyCheckoutModal(true);
      return;
    }
    handleCheckOutCameraOpen();
  };

  const performCheckOut = async (photoDataUrl?: string): Promise<void> => {
    try {
      await checkOut(photoDataUrl);
      toast.success(t.checkedOutToast, { description: t.checkedOutDesc });
    } catch (error) {
      toast.error(t.checkOutFailed, { description: (error as Error).message });
    }
  };

  const handleRefresh = async (): Promise<void> => {
    try {
      await refresh();
      toast.success(t.refreshed, { description: t.refreshedDesc });
    } catch {
      toast.error(t.refreshFailed, { description: t.refreshFailedDesc });
    }
  };

  const getStatusColor = (status: AttendanceRecord['status']): StatusColor => {
    switch (status) {
      case 'on_time': return 'success';
      case 'late': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: AttendanceRecord['status']): string => {
    switch (status) {
      case 'on_time': return t.onTime;
      case 'late': return t.late;
      default: return t.notCheckedInYet;
    }
  };

  const greeting = getGreeting(currentTime.getHours());
  const GreetingIcon = greeting.icon;
  const greetingText = (() => {
    const hour = currentTime.getHours();
    if (hour < 6) return t.greetNight;
    if (hour < 12) return t.greetMorning;
    if (hour < 17) return t.greetAfternoon;
    if (hour < 19) return t.greetEvening;
    return t.greetNight;
  })();
  const shiftProgress = getShiftProgressPercent(shiftSettings.startTime, shiftSettings.endTime, todayStatus?.date);

  // Weekly attendance streak
  const weeklyStreak = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const days: Array<{ label: string; status: 'on_time' | 'late' | 'absent' | 'future' }> = [];
    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i);
      const dayStr = format(day, 'yyyy-MM-dd');
      const dayLabel = format(day, 'EEE');
      const isFuture = day > new Date();
      const record = history.find((r) => r.date === dayStr);
      if (isFuture) {
        days.push({ label: dayLabel, status: 'future' });
      } else if (record) {
        days.push({ label: dayLabel, status: record.status });
      } else {
        // Weekend or no record (could be holiday)
        const dayOfWeek = day.getDay();
        days.push({ label: dayLabel, status: dayOfWeek === 0 || dayOfWeek === 6 ? 'future' : 'absent' });
      }
    }
    return days;
  }, [history]);

  const onTimeRate = monthlySummary && monthlySummary.totalDays > 0
    ? Math.round((monthlySummary.onTimeDays / monthlySummary.totalDays) * 100)
    : 0;

  const showNotifBanner =
    notificationManager.isSupported() &&
    notifPermission === 'default' &&
    !notificationManager.getSettings().enabled;

  const earlyLeaveMinutes = getEarlyLeaveMinutes(shiftSettings.startTime, shiftSettings.endTime);

  // Format early leave minutes into a readable string
  const earlyLeaveLabel = earlyLeaveMinutes >= 60
    ? `${Math.floor(earlyLeaveMinutes / 60)}h ${earlyLeaveMinutes % 60}m`
    : `${earlyLeaveMinutes} min`;

  return (
    <div className="space-y-4 animate-fade-in pb-8">
      <CameraCapture
        open={showCamera}
        onCapture={(photo) => void handleCameraCapture(photo)}
        onClose={handleCameraClose}
        title={cameraMode === 'check_out' ? t.checkOutCameraTitle : t.cameraTitle}
      />

      {/* ── Early Check-Out Modal ── */}
      <AlertDialog open={showEarlyCheckoutModal} onOpenChange={setShowEarlyCheckoutModal}>
        <AlertDialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
          {/* Gradient header */}
          <div
            className="relative overflow-hidden px-5 py-5"
            style={{ background: 'linear-gradient(135deg, hsl(var(--warning)) 0%, hsl(35 90% 42%) 100%)' }}
          >
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-black/10" />
            <div className="relative flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 shadow-inner flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-white/70 uppercase tracking-[0.15em]">ABSENSI</p>
                <p className="text-[15px] font-black text-white leading-tight">{t.earlyCheckout}</p>
              </div>
            </div>
          </div>
          {/* Body */}
          <div className="px-5 py-5 space-y-3">
            <p className="text-sm text-muted-foreground">{t.earlyCheckoutDesc(shiftSettings.endTime, earlyLeaveLabel)}</p>
            <div className="p-3.5 rounded-xl border" style={{ background: 'hsl(var(--warning) / 0.08)', borderColor: 'hsl(var(--warning) / 0.25)' }}>
              <p className="text-xs font-semibold" style={{ color: 'hsl(var(--warning))' }}>{t.earlyCheckoutWarning}</p>
            </div>
            <p className="text-sm text-muted-foreground">{t.earlyCheckoutConfirm}</p>
            <div className="flex flex-col gap-2.5 pt-1">
              <AlertDialogAction
                onClick={() => { setShowEarlyCheckoutModal(false); handleCheckOutCameraOpen(); }}
                className="w-full h-11 rounded-xl font-bold inline-flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, hsl(var(--warning)) 0%, hsl(35 90% 42%) 100%)', color: '#fff' }}
              >
                <LogOut className="h-4 w-4" />
                {t.checkOutEarly}
              </AlertDialogAction>
              <AlertDialogCancel className="w-full rounded-xl h-11">{t.stayWorking}</AlertDialogCancel>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Check-In Blocked Modal ── */}
      <AlertDialog open={showCheckInBlockedModal} onOpenChange={setShowCheckInBlockedModal}>
        <AlertDialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
          {/* Gradient header */}
          <div
            className="relative overflow-hidden px-5 py-5"
            style={{ background: 'linear-gradient(135deg, hsl(var(--destructive)) 0%, #991b1b 100%)' }}
          >
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-black/10" />
            <div className="relative flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 shadow-inner flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-white/70 uppercase tracking-[0.15em]">ABSENSI</p>
                <p className="text-[15px] font-black text-white leading-tight">{t.checkInBlockedTitle}</p>
              </div>
            </div>
          </div>
          {/* Body */}
          <div className="px-5 py-5 space-y-3">
            <p className="text-sm text-muted-foreground">{t.checkInBlockedSubtitle}</p>
            {hasSiteAssigned === false && (
              <div className="flex items-start gap-3 p-3.5 rounded-xl border" style={{ background: 'hsl(var(--destructive) / 0.05)', borderColor: 'hsl(var(--destructive) / 0.25)' }}>
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: 'hsl(var(--destructive) / 0.15)' }}>
                  <MapPin className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">{t.checkInBlockedNoSite}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{t.checkInBlockedNoSiteDesc}</p>
                </div>
              </div>
            )}
            {hasShiftAssigned === false && (
              <div className="flex items-start gap-3 p-3.5 rounded-xl border" style={{ background: 'hsl(var(--destructive) / 0.05)', borderColor: 'hsl(var(--destructive) / 0.25)' }}>
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: 'hsl(var(--destructive) / 0.15)' }}>
                  <Clock className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">{t.checkInBlockedNoShift}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{t.checkInBlockedNoShiftDesc}</p>
                </div>
              </div>
            )}
            <div className="p-3 rounded-xl bg-muted/50 border border-border/50">
              <p className="text-xs text-muted-foreground leading-relaxed">{t.checkInBlockedContact}</p>
            </div>
            <AlertDialogAction
              onClick={() => setShowCheckInBlockedModal(false)}
              className="w-full h-11 rounded-xl font-bold inline-flex items-center justify-center"
              style={{ background: 'var(--gradient-primary)', color: '#fff' }}
            >
              {t.checkInBlockedUnderstood}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* ═══════════════════════════════════════
          HERO HEADER
      ═══════════════════════════════════════ */}
      <div
        className="relative rounded-2xl overflow-hidden shadow-lg animate-fade-in-up stagger-1"
        style={{ background: 'var(--gradient-primary)' }}
      >
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-12 -left-8 h-40 w-40 rounded-full bg-black/10" />
        <div className="pointer-events-none absolute top-6 right-28 h-20 w-20 rounded-full bg-white/8" />

        <div className="relative px-5 pt-5 pb-5">
          {/* Top row: greeting + refresh */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/20">
                <GreetingIcon className="h-4 w-4 text-white" />
              </div>
              <span className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{greetingText}</span>
            </div>
            <div className="flex items-center gap-2">
              {notifPermission === 'granted' && notificationManager.getSettings().enabled && (
                <div
                  className="flex items-center gap-1 rounded-full px-2 py-1 text-[10px] text-white/80 font-semibold"
                  style={{ background: 'rgba(255,255,255,0.15)' }}
                  data-testid="notifications-active-badge"
                >
                  <Bell className="h-3 w-3" />
                  {t.remindersOn}
                </div>
              )}
              <button
                onClick={() => void handleRefresh()}
                disabled={loading}
                className="flex h-8 w-8 items-center justify-center rounded-xl transition-all"
                style={{ background: 'rgba(255,255,255,0.18)' }}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 text-white ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Name + clock */}
          <div className="flex items-end justify-between gap-3">
            <div>
              <h1 className="text-[28px] font-black text-white leading-none tracking-tight">
                {user?.fullName?.split(' ')[0]}
              </h1>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <p className="text-[12px] text-white/65 font-medium">{getDayLabel(currentTime)}</p>
                {(shiftSettings.name || shiftSettings.startTime) && (
                  <>
                    <span className="text-white/30 text-[10px]">•</span>
                    <span className="text-[11px] text-white/70 font-semibold">{shiftSettings.name || t.regularShift}</span>
                    <span className="text-white/30 text-[10px]">•</span>
                    <span className="text-[11px] text-white/80 font-black tabular-nums">{shiftSettings.startTime || '--:--'} – {shiftSettings.endTime || '--:--'}</span>
                  </>
                )}
              </div>
            </div>
            {/* Live clock display */}
            <div
              className="text-right rounded-2xl px-4 py-2.5"
              style={{ background: 'rgba(0,0,0,0.18)' }}
            >
              <p className="text-2xl font-black text-white leading-none tabular-nums tracking-tight">
                {format(currentTime, 'HH:mm')}
              </p>
              <p className="text-[10px] text-white/60 mt-0.5 font-semibold tabular-nums">
                {format(currentTime, 'ss')}s
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* ── Notification Banner ── */}
      {showNotifBanner && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border p-3.5"
          style={{ background: 'rgba(240,130,0,0.06)', borderColor: 'rgba(240,130,0,0.25)' }}
          data-testid="notification-banner"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: 'var(--gradient-primary)' }}>
              <Bell className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{t.enableReminders}</p>
              <p className="text-[11px] text-muted-foreground">{t.enableRemindersDesc}</p>
            </div>
          </div>
          <button
            onClick={() => void handleEnableNotifications()}
            className="flex-shrink-0 rounded-lg px-3.5 py-1.5 text-xs font-bold text-white shadow-sm"
            style={{ background: 'var(--gradient-primary)' }}
            data-testid="enable-notifications-btn"
          >
            {t.enable}
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════
          TODAY'S ATTENDANCE CARD
      ═══════════════════════════════════════ */}
      <Card className="border border-border/60 bg-white dark:bg-card shadow-sm overflow-hidden animate-fade-in-up stagger-2">
        {/* Card top accent bar */}
        <div
          className="h-1 w-full"
          style={{
            background: activeStatus?.clockOut && minutesUntilShiftStart > threshold
              ? 'linear-gradient(90deg, #10b981, #34d399)'
              : activeStatus?.clockIn
              ? 'var(--gradient-primary)'
              : 'hsl(var(--muted))',
          }}
        />
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-bold">
              <div
                className="flex h-7 w-7 items-center justify-center rounded-lg"
                style={{ background: 'var(--gradient-primary)' }}
              >
                <Clock className="h-3.5 w-3.5 text-white" />
              </div>
              {t.todaysAttendance}
            </CardTitle>
            {activeStatus && minutesUntilShiftStart > threshold && (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide text-white"
                style={{
                  backgroundColor:
                    activeStatus.status === 'on_time' ? '#10b981'
                    : activeStatus.status === 'late' ? '#f59e0b'
                    : '#6b7280',
                }}
              >
                {getStatusLabel(activeStatus.status)}
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="px-4 pb-5">
          {/* ── State 1: Not checked in (show check-in button) ── */}
          {showCheckInButton ? (
            <div className="flex flex-col items-center py-8 text-center">
              {/* Icon */}
              <div className="relative mb-5">
                <div
                  className="h-24 w-24 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--gradient-primary)', opacity: 0.12 }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="h-20 w-20 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--gradient-primary)', opacity: 0.18 }}
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Camera className="h-9 w-9 text-primary opacity-70" />
                </div>
              </div>
              <h3 className="text-lg font-bold mb-1">{t.notCheckedInYet}</h3>
              <p className="text-sm text-muted-foreground mb-1">{t.notCheckedInDesc}</p>
              <p className="text-[11px] text-muted-foreground mb-7">{t.photoVerification}</p>
              <button
                onClick={handleCheckInClick}
                disabled={loading}
                className="flex items-center gap-2.5 rounded-2xl px-8 py-3.5 text-sm font-black text-white shadow-md transition-all active:scale-95 disabled:opacity-60"
                style={{ background: 'var(--gradient-primary)', boxShadow: '0 4px 16px rgba(232,130,0,0.4)' }}
                data-testid="check-in-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
                {t.checkInNow}
              </button>
            </div>
          ) : activeStatus?.clockOut ? (
            /* ── State 3: Work complete ── */
            <div className="pt-3">
              {/* Completion header */}
              <div className="flex items-center gap-3 mb-5 p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30">
                <div className="relative flex-shrink-0">
                  {activeStatus?.checkInRecord?.photoDataUrl ? (
                    <img
                      src={activeStatus?.checkInRecord.photoDataUrl}
                      alt="Check-in photo"
                      className="h-14 w-14 rounded-xl object-cover border-2 border-emerald-400"
                      data-testid="checkin-photo-thumbnail"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <CheckCircle className="h-7 w-7 text-emerald-500" />
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center">
                    <CheckCircle className="h-3 w-3 text-white" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">{t.workComplete}</p>
                  <p className="text-[11px] text-muted-foreground">{t.workCompleteDesc}</p>
                </div>
              </div>
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2.5">
                {([
                  { label: t.checkIn, val: formatTime(activeStatus?.clockIn), icon: LogIn, color: '#10b981', bg: 'rgba(16,185,129,0.08)' },
                  { label: t.checkOut, val: formatTime(activeStatus?.clockOut!), icon: LogOut, color: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },
                  { label: t.duration, val: workingDuration, icon: Timer, color: 'hsl(var(--primary))', bg: 'rgba(240,130,0,0.08)' },
                ] as const).map(({ label, val, icon: Icon, color, bg }) => (
                  <div key={label} className="rounded-xl p-3 text-center border border-border/40" style={{ background: bg }}>
                    <div
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg mb-1.5"
                      style={{ background: bg, color }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <p className="text-[10px] text-muted-foreground mb-0.5">{label}</p>
                    <p className="text-sm font-black" style={{ color }}>{val}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* ── State 2: Currently working ── */
            <div className="pt-3">
              {/* Working header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-shrink-0">
                  {activeStatus?.checkInRecord?.photoDataUrl ? (
                    <img
                      src={activeStatus?.checkInRecord.photoDataUrl}
                      alt="Check-in photo"
                      className="h-14 w-14 rounded-xl object-cover border-2 border-primary"
                      data-testid="working-photo-thumbnail"
                    />
                  ) : (
                    <div
                      className="h-14 w-14 rounded-xl flex items-center justify-center"
                      style={{ background: 'var(--gradient-primary)' }}
                    >
                      <Clock className="h-7 w-7 text-white" />
                    </div>
                  )}
                  {/* Pulse dot */}
                  <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                    <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-primary" />
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-foreground">{t.currentlyWorking}</p>
                  <p className="text-[11px] text-muted-foreground">{t.currentlyWorkingDesc}</p>
                </div>
                {/* Live duration — prominent */}
                <div
                  className="flex-shrink-0 rounded-xl px-3 py-2 text-right"
                  style={{ background: 'var(--gradient-primary)' }}
                >
                  <p className="text-[9px] text-white/70 font-bold uppercase tracking-widest">{t.workingDuration}</p>
                  <p className="text-lg font-black text-white tabular-nums leading-tight">{workingDuration}</p>
                </div>
              </div>

              {/* Shift progress */}
              {shiftSettings.startTime && shiftSettings.endTime && (
                <div className="mb-4 p-3.5 rounded-xl border border-border/40 bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">{t.shiftProgress}</span>
                    <span className="text-[11px] font-black text-primary">{shiftProgress}%</span>
                  </div>
                  {/* Custom gradient progress bar */}
                  <div className="h-2 w-full rounded-full bg-muted/60 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${shiftProgress}%`, background: 'var(--gradient-primary)' }}
                    />
                  </div>
                  <div className="flex justify-between mt-1.5">
                    <span className="text-[10px] text-muted-foreground font-semibold tabular-nums">{shiftSettings.startTime}</span>
                    <span className="text-[10px] text-muted-foreground font-semibold tabular-nums">{shiftSettings.endTime}</span>
                  </div>
                </div>
              )}

              {/* Check-in time */}
              <div className="flex items-center gap-2 p-3 mb-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                  <LogIn className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">{t.checkInTime}</p>
                  <p className="text-sm font-black text-emerald-600 dark:text-emerald-400 tabular-nums">{formatTime(activeStatus?.clockIn)}</p>
                </div>
              </div>

              {/* Check-out button */}
              <button
                onClick={() => handleCheckOut()}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2.5 rounded-2xl py-3.5 text-sm font-black text-white transition-all active:scale-[0.98] disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                  boxShadow: '0 4px 16px rgba(245,158,11,0.38)',
                }}
                data-testid="check-out-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogOut className="h-5 w-5" />}
                {t.checkOutBtn}
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════
          LIVE LOCATION MAP
      ═══════════════════════════════════════ */}
      <Card className="border border-border/60 bg-white dark:bg-card shadow-sm overflow-hidden animate-fade-in-up stagger-3">
        <div className="h-1 w-full" style={{ background: 'var(--gradient-primary)' }} />
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-bold">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-lg"
              style={{ background: 'var(--gradient-primary)' }}
            >
              <MapPin className="h-3.5 w-3.5 text-white" />
            </div>
            {t.liveLocation}
          </CardTitle>
          <CardDescription className="text-xs mt-0.5">{t.liveLocationDesc}</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <Suspense
            fallback={
              <div
                className="flex items-center justify-center bg-muted/30 rounded-xl border border-border"
                style={{ height: 280 }}
                data-testid="map-loading"
              >
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">{t.loadingMap}</p>
                </div>
              </div>
            }
          >
            <AttendanceMap officeSettings={officeSettings} />
          </Suspense>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════
          THIS WEEK STREAK
      ═══════════════════════════════════════ */}
      <Card className="border border-border/60 bg-white dark:bg-card shadow-sm overflow-hidden animate-fade-in-up stagger-4">
        <div className="h-1 w-full" style={{ background: 'var(--gradient-primary)' }} />
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-bold">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-lg"
              style={{ background: 'var(--gradient-primary)' }}
            >
              <Target className="h-3.5 w-3.5 text-white" />
            </div>
            {t.thisWeek}
          </CardTitle>
          <CardDescription className="text-xs mt-0.5">{t.weeklyStreak}</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="flex items-end justify-between gap-1">
            {weeklyStreak.map((day, i) => {
              const isOnTime = day.status === 'on_time';
              const isLate = day.status === 'late';
              const isAbsent = day.status === 'absent';
              const isFuture = day.status === 'future';
              return (
                <div key={i} className="flex flex-col items-center gap-1.5 flex-1">
                  <span className="text-[10px] font-bold text-muted-foreground">{day.label}</span>
                  <div
                    className="h-9 w-9 rounded-xl flex items-center justify-center text-xs font-bold transition-all shadow-sm"
                    style={{
                      background: isOnTime
                        ? 'linear-gradient(135deg, #10b981, #34d399)'
                        : isLate
                        ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
                        : isAbsent
                        ? 'rgba(239,68,68,0.12)'
                        : 'hsl(var(--muted))',
                      color: isOnTime ? '#fff' : isLate ? '#fff' : isAbsent ? '#ef4444' : 'hsl(var(--muted-foreground))',
                    }}
                  >
                    {isOnTime ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : isLate ? (
                      <AlertCircle className="h-4 w-4" />
                    ) : isAbsent ? (
                      <span className="text-xs font-black">✕</span>
                    ) : (
                      <span className="text-sm">·</span>
                    )}
                  </div>
                  {/* Status dot */}
                  <div
                    className="h-1.5 w-1.5 rounded-full"
                    style={{
                      background: isOnTime ? '#10b981' : isLate ? '#f59e0b' : isAbsent ? '#ef4444' : 'hsl(var(--muted))',
                    }}
                  />
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/40">
            {([
              { color: '#10b981', label: t.onTime },
              { color: '#f59e0b', label: t.late },
              { color: '#ef4444', label: t.absent },
            ] as const).map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
                <div className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
                {label}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════
          MONTHLY PERFORMANCE
      ═══════════════════════════════════════ */}
      {monthlySummary && (
        <Card className="border border-border/60 bg-white dark:bg-card shadow-sm overflow-hidden animate-fade-in-up stagger-5">
          <div className="h-1 w-full" style={{ background: 'var(--gradient-primary)' }} />
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="flex items-center gap-2 text-sm font-bold">
              <div
                className="flex h-7 w-7 items-center justify-center rounded-lg"
                style={{ background: 'var(--gradient-primary)' }}
              >
                <TrendingUp className="h-3.5 w-3.5 text-white" />
              </div>
              {t.performance}
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">{t.thisMonthRate}</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-5">
            {/* Circular SVG + stat rows side by side */}
            <div className="flex items-center gap-5">
              {/* Circular progress */}
              <div className="flex-shrink-0 relative h-28 w-28">
                <svg className="h-28 w-28 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" stroke="hsl(var(--muted))" strokeWidth="8" fill="none" />
                  <circle
                    cx="50" cy="50" r="42"
                    stroke="url(#perfGradient)"
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${onTimeRate * 2.64} ${264 - onTimeRate * 2.64}`}
                    className="transition-all duration-1000 ease-out"
                  />
                  <defs>
                    <linearGradient id="perfGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="hsl(28 80% 52%)" />
                      <stop offset="100%" stopColor="hsl(10 70% 50%)" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-black text-foreground">{onTimeRate}<span className="text-base">%</span></span>
                  <span className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wide">{t.onTime}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex-1 space-y-2.5">
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30">
                  <div className="flex items-center gap-2">
                    <Award className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="text-xs font-semibold text-foreground">{t.onTimeDays}</span>
                  </div>
                  <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{monthlySummary.onTimeDays}</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                    <span className="text-xs font-semibold text-foreground">{t.lateDays}</span>
                  </div>
                  <span className="text-sm font-black text-amber-600 dark:text-amber-400">{monthlySummary.lateDays}</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl border border-border/40 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Zap className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-semibold text-foreground">{t.totalHours}</span>
                  </div>
                  <span className="text-sm font-black text-primary">{monthlySummary.totalHours}h</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
